//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import type { CSSProperties } from "react";
import { useState } from "react";
import { Button, Form, Icon, Modal, Table } from "semantic-ui-react";
import { Duration, Timestamp } from "../../api/googleTypes";
import { VolumeInfo as ApiVolumeInfo } from "../../api/lib";
import { DiskPerformanceInput } from "../../ui/lib";
import { CommentCreationArgs } from "../comments/CommentTypes";
import { AsyncResult } from "../../util/Types";
import { IWithRefreshProps } from "../../util/WithRefresh";
import { DiskRateLimitHint } from "./DiskRateLimitHint";
import { DiskSizeEditModal } from "./DiskSizeView";
import { VolumeInfoView } from "./VolumeInfosViews";

const mono: CSSProperties = { fontFamily: "monospace", fontSize: 12 };

export interface IRoleDiskColumnsArgs extends CommentCreationArgs, IWithRefreshProps {
  kind: "none" | "dbserver" | "agent";
  diskSize?: number;
  diskPerformanceId?: string;
  regionId: string;
  nodeSizeId?: string;
  nodeDiskSize?: number;
  volumeInfo?: ApiVolumeInfo;
  lastDiskPerformanceUpdatedAt?: Timestamp;
  lastDiskSizeUpdatedAt?: Timestamp;
  diskRateLimitPeriod?: Duration;
  diskRateLimitActive?: Duration;
  canEditDiskSize: boolean;
  canEditDiskSizeIgnoreMax: boolean;
  onUpdateDiskSize: (newValue: number, ignoreMax: boolean) => Promise<AsyncResult<void>>;
  canEditDiskPerformance: boolean;
  onEditDiskPerformance: () => void;
  onCancelEditDiskPerformance: () => void;
  editDiskPerformance: boolean;
  onDiskPerformanceChange: (id: string) => void;
  canEditVolumeInfos: boolean;
  onEditVolumeInfos: () => void;
  onCancelEditVolumeInfos: () => void;
  editVolumeInfos: boolean;
  onSaveVolumeInfos: (newVolumeType: string, newVolumeIOPS: number, newVolumeThroughput: number) => Promise<{ errorMessage: string }>;
  processingUpdateVolumeInfos: boolean;
}

/** Three trailing table cells: Disk size | Performance | Throughput. */
export const RoleDiskColumns = (args: IRoleDiskColumnsArgs) => {
  const [perfOpen, setPerfOpen] = useState(false);

  if (args.kind === "none") {
    return (
      <>
        <Table.Cell style={{ color: "#9ca3af" }}>—</Table.Cell>
        <Table.Cell style={{ color: "#9ca3af" }}>—</Table.Cell>
        <Table.Cell style={{ color: "#9ca3af" }}>—</Table.Cell>
      </>
    );
  }

  const sizeLabel = args.diskSize != null ? `${args.diskSize} GiB` : "—";
  const perfLabel = args.kind === "dbserver" ? args.diskPerformanceId || "—" : undefined;
  const thruRaw = args.volumeInfo?.throughput;
  const thruLabel = thruRaw != null && thruRaw !== 0 ? `${thruRaw}` : "default";

  return (
    <>
      <Table.Cell>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={mono}>{sizeLabel}</span>
          {args.canEditDiskSize && (
            <DiskSizeEditModal
              diskSize={args.diskSize}
              canIgnoreMax={args.canEditDiskSizeIgnoreMax}
              onUpdateDiskSize={args.onUpdateDiskSize}
              createComment={args.createComment}
              lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
              lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
              diskRateLimitPeriod={args.diskRateLimitPeriod}
              diskRateLimitActive={args.diskRateLimitActive}
            />
          )}
        </span>
      </Table.Cell>

      <Table.Cell>
        {args.kind === "dbserver" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={mono}>{perfLabel}</span>
            {args.canEditDiskPerformance && (
              <Icon
                name="pencil"
                className="edit-pencil"
                onClick={() => {
                  args.onEditDiskPerformance();
                  setPerfOpen(true);
                }}
              />
            )}
            <Modal
              open={perfOpen && args.editDiskPerformance}
              onClose={() => {
                setPerfOpen(false);
                args.onCancelEditDiskPerformance();
              }}
              size="tiny"
            >
              <Modal.Header>Edit disk performance</Modal.Header>
              <Modal.Content>
                <DiskRateLimitHint
                  lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
                  lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
                  diskRateLimitPeriod={args.diskRateLimitPeriod}
                  diskRateLimitActive={args.diskRateLimitActive}
                />
                <Form>
                  <Form.Field>
                    <DiskPerformanceInput
                      {...args}
                      placeholder="disk performance"
                      id={args.diskPerformanceId}
                      onChange={(id: string) => {
                        args.onDiskPerformanceChange(id);
                        setPerfOpen(false);
                      }}
                      notAll
                      regionId={args.regionId}
                      nodeSizeId={args.nodeSizeId}
                      nodeDiskSize={args.nodeDiskSize}
                    />
                  </Form.Field>
                </Form>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  negative
                  content="Cancel"
                  onClick={() => {
                    setPerfOpen(false);
                    args.onCancelEditDiskPerformance();
                  }}
                />
              </Modal.Actions>
            </Modal>
          </span>
        ) : (
          <span style={{ color: "#9ca3af" }}>—</span>
        )}
      </Table.Cell>

      <Table.Cell>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={mono}>{thruLabel}</span>
          {args.canEditVolumeInfos && <Icon name="pencil" className="edit-pencil" onClick={() => args.onEditVolumeInfos()} />}
        </span>
        <VolumeInfoView
          title={args.kind === "dbserver" ? "DBServer" : "Agent"}
          volumeType={args.volumeInfo?.type}
          volumeIOPS={args.volumeInfo?.iops}
          volumeThroughput={args.volumeInfo?.throughput}
          canEdit={false}
          hideSummary
          lastDiskPerformanceUpdatedAt={args.lastDiskPerformanceUpdatedAt}
          lastDiskSizeUpdatedAt={args.lastDiskSizeUpdatedAt}
          diskRateLimitPeriod={args.diskRateLimitPeriod}
          diskRateLimitActive={args.diskRateLimitActive}
          onEdit={args.onEditVolumeInfos}
          editing={args.editVolumeInfos}
          onCancelEdit={args.onCancelEditVolumeInfos}
          onSaveEdit={args.onSaveVolumeInfos}
          processingUpdate={args.processingUpdateVolumeInfos}
          createComment={args.createComment}
        />
      </Table.Cell>
    </>
  );
};
